import { AppSettings, Asset, ASSET_TYPE, Stream } from '@24i/nxg-sdk-photon';
import { getRandomInt } from '@24i/nxg-sdk-smartott-stubs/src/utils';
import { AdvertisementParameters } from './advertisementParameters';
import { getUrlFromParameterManager } from './urlComposerClient';
import { SafeURL } from '../../utils/safeUrl';

const adjustUrl = (
    original?: string | null | undefined,
    replacements?: Record<string, string | null | undefined>,
    queryParameters?: Record<string, string | null | undefined>
): string | null | undefined => {
    if (!original) return original;

    let withReplacements = original;
    if (replacements) {
        Object.keys(replacements).forEach((name) => {
            const value = replacements[name];
            if (value || typeof value === 'string') {
                withReplacements = withReplacements.replace(`{{${name}}}`, value);
            }
        });
    }

    const withParameters = new SafeURL(withReplacements);
    if (queryParameters) {
        Object.keys(queryParameters).forEach((name) => {
            const value = queryParameters[name];
            if (value || typeof value === 'string') {
                withParameters.searchParams.set(name, value);
            }
        });
    }
    return withParameters.toString();
};

const buildThunderstormPlaceholders = (
    parameters: AdvertisementParameters,
    asset: Asset
): Record<string, string> => {
    /* eslint-disable @typescript-eslint/dot-notation */
    const urlPlaceholders: Record<string, string> = {};
    urlPlaceholders['RANDOM_NUMBER'] = getRandomInt(0, Number.MAX_SAFE_INTEGER).toString();
    urlPlaceholders['IP'] = parameters.device?.ip || '';
    urlPlaceholders['USER_AGENT'] = navigator?.userAgent || '';
    urlPlaceholders['DEVICE_ID'] = parameters.device?.id || '';
    urlPlaceholders['ASSET_LANGUAGE'] = parameters.app?.language || '';
    urlPlaceholders['ASSET_LIVESTREAM'] =
        asset.isLive ||
        asset.type === ASSET_TYPE.CHANNEL ||
        asset.type === ASSET_TYPE.BROADCAST ||
        asset.type === ASSET_TYPE.LIVE_EVENT
            ? '1'
            : '0';
    const gdpr = parameters.dataPrivacy?.regulation === 'gdpr';
    const ccpa = parameters.dataPrivacy?.regulation === 'ccpa';
    urlPlaceholders['GDPR'] = gdpr ? '1' : '0';
    if (gdpr) {
        urlPlaceholders['GDPR_CONSENT'] = parameters.dataPrivacy?.consent || '';
    } else {
        urlPlaceholders['GDPR_CONSENT'] = '';
    }

    if (ccpa) {
        urlPlaceholders['CCPA_CONSENT'] = parameters.dataPrivacy?.consent || '';
    } else {
        urlPlaceholders['CCPA_CONSENT'] = '';
    }

    urlPlaceholders['CCPA_CONSENT'] = '';
    urlPlaceholders['LAT'] = `${parameters.dataPrivacy?.lat}`;
    urlPlaceholders['DNT'] = `${parameters.dataPrivacy?.dnt}`;
    return urlPlaceholders;
    /* eslint-enable @typescript-eslint/dot-notation */
};

export const buildAdjustedStream = async (
    adInsertionConfig: AppSettings['features']['adInsertion'],
    original: Stream,
    asset: Asset,
    advertisementParameters?: AdvertisementParameters,
    palSdkNonce?: string
): Promise<Stream> => {
    const serializedClientData = advertisementParameters
        ? JSON.stringify(advertisementParameters)
        : undefined;
    const urlEncodedClientData = serializedClientData
        ? encodeURIComponent(serializedClientData)
        : undefined;
    const platform = advertisementParameters?.platform;

    if ((original.advertisement || original.data?.adsUrl) && adInsertionConfig?.type === 'CSAI') {
        return {
            ...original,
            advertisement: adjustUrl(
                original.advertisement,
                {},
                {
                    clientData: serializedClientData,
                    gam_nonce: palSdkNonce,
                }
            ),
            data: {
                ...original.data,
                adsUrl: adjustUrl(
                    original.data?.adsUrl,
                    {},
                    {
                        clientData: serializedClientData,
                        gam_nonce: palSdkNonce,
                    }
                ),
            },
        };
    }

    if (original.data?.sessionUrl && adInsertionConfig?.SSAIProvider === 'mediaTailor') {
        return {
            ...original,
            data: {
                ...original.data,
                adsParams: {
                    ...original.data?.adsParams,
                    'ads.clientData': urlEncodedClientData,
                    'ads.platform': platform,
                    gam_nonce: palSdkNonce,
                },
            },
        };
    }

    if (advertisementParameters && adInsertionConfig?.SSAIProvider === 'thunderstorm') {
        const replacements = buildThunderstormPlaceholders(advertisementParameters, asset);
        return {
            ...original,
            url: adjustUrl(original.url, replacements, {
                gam_nonce: palSdkNonce,
            }),
        };
    }

    if (original.urlSrc) {
        const parametrizedUrl = adjustUrl(
            original.urlSrc,
            {},
            {
                clientData: serializedClientData,
            }
        );
        if (parametrizedUrl) {
            const composedUrl = await getUrlFromParameterManager(parametrizedUrl);
            if (composedUrl) {
                return {
                    ...original,
                    url: composedUrl,
                };
            }
        }
    }

    if (adInsertionConfig?.SSAIProvider === 'mediaTailor') {
        return {
            ...original,
            url: adjustUrl(
                original.url,
                {},
                {
                    'ads.clientData': serializedClientData,
                    'ads.platform': platform,
                    gam_nonce: palSdkNonce,
                }
            ),
        };
    }

    return {
        ...original,
        url: adjustUrl(
            original.url,
            {},
            {
                gam_nonce: palSdkNonce,
            }
        ),
    };
};
